// FILE: ActiveSubagentsIndicator.logic.ts
// Purpose: Turn the active subagent tasks into the rows the indicator renders.
// Exports: list/summary builders for ActiveSubagentsIndicator.

import type { ThreadId } from "@taskin-code/contracts";

import type { ActiveSubagentTask } from "../../session-logic";

const MAX_LABEL_LENGTH = 40;
const MAX_DETAIL_LENGTH = 80;

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

/** "code-reviewer" if we have it, else a truncated description, else nothing worth naming. */
export function activeSubagentLabel(task: ActiveSubagentTask): string | null {
  if (task.subagentType) {
    return task.subagentType;
  }
  if (task.description) {
    return truncate(task.description, MAX_LABEL_LENGTH);
  }
  return null;
}

/**
 * One row of the list, already resolved: `threadId` is set only when the entry
 * owns a thread the user can open (a managed-agent delegation), which is what
 * decides whether its row navigates or just scrolls the transcript into view.
 *
 * `startedAt` drives the row's live clock, and `awaitingParent` marks the rows
 * that are *not* work in flight — a delegation that already answered.
 */
export interface ActiveSubagentListItem {
  key: string;
  label: string;
  detail?: string;
  threadId?: ThreadId;
  startedAt?: string;
  awaitingParent?: boolean;
}

export function buildActiveSubagentListItems(
  tasks: ReadonlyArray<ActiveSubagentTask>,
): ReadonlyArray<ActiveSubagentListItem> {
  return tasks.map((task) => {
    const label = activeSubagentLabel(task);
    // The description is only worth a second line when it is not already what
    // the row is called.
    const detail =
      task.description && task.description !== label
        ? truncate(task.description, MAX_DETAIL_LENGTH)
        : undefined;
    return {
      key: task.key,
      label: label ?? "Subagent",
      ...(detail ? { detail } : {}),
      ...(task.threadId ? { threadId: task.threadId } : {}),
      ...(task.startedAt ? { startedAt: task.startedAt } : {}),
      ...(task.awaitingParent ? { awaitingParent: true } : {}),
    };
  });
}

/**
 * The one-line summary the collapsed indicator shows.
 *
 * Running and delivered are counted apart on purpose: folding a delegation that
 * already answered into "2 subagents running" would report work that is not
 * happening, which is the very confusion this indicator exists to remove.
 */
export function summarizeActiveSubagents(
  tasks: ReadonlyArray<ActiveSubagentTask>,
  awaitingTasks: ReadonlyArray<ActiveSubagentTask> = [],
): string {
  const running = tasks.filter((task) => !task.awaitingParent);
  const names = running
    .map((task) => activeSubagentLabel(task))
    .filter((name): name is string => name !== null);

  const parts: string[] = [];
  if (running.length > 0) {
    const headline =
      running.length === 1 ? "1 subagent running" : `${running.length} subagents running`;
    parts.push(names.length > 0 ? `${headline} · ${names.join(", ")}` : headline);
  }
  if (awaitingTasks.length > 0) {
    const awaitingNames = awaitingTasks
      .map((task) => activeSubagentLabel(task))
      .filter((name): name is string => name !== null);
    const headline =
      awaitingTasks.length === 1 ? "1 informe listo" : `${awaitingTasks.length} informes listos`;
    parts.push(awaitingNames.length > 0 ? `${headline} · ${awaitingNames.join(", ")}` : headline);
  }
  // Both empty cannot happen: the indicator is not rendered with nothing to say.
  return parts.join(" — ");
}

/** The start of the longest-running task, for the collapsed row's clock. */
export function earliestStartedAt(tasks: ReadonlyArray<ActiveSubagentTask>): string | null {
  let earliest: string | null = null;
  let earliestMs = Number.POSITIVE_INFINITY;
  for (const task of tasks) {
    if (task.awaitingParent || !task.startedAt) continue;
    const startedMs = Date.parse(task.startedAt);
    if (Number.isNaN(startedMs) || startedMs >= earliestMs) continue;
    earliest = task.startedAt;
    earliestMs = startedMs;
  }
  return earliest;
}
